import React, { useCallback } from 'react'
import { Box } from '@mui/material'
import { SparklesIcon } from '@heroicons/react/24/outline'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { isGamerUpcomingActivitiesPagePath } from '../../../config/routes/routeConfig'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { TESTIDS } from '../../../config/testids'
import { INTERAX_COLORS } from '../../../constants/colors'
import { COMPONENT_DIMENSIONS } from '../../../constants/layout'
import { NotificationCard } from './NotificationCard'

interface DebriefStartedNotificationProps {
  onDismiss: () => void
}

/** Shown in the call while the team's AI debrief runs on its own page; opening it goes there. */
export const DebriefStartedNotification: React.FC<DebriefStartedNotificationProps> = ({ onDismiss }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const lobbyButtonLabel = t('gamer.buttons.enterReflectionLobby', 'Enter reflection lobby')
  const lobbyPageName = t('gamer.navigation.upcomingActivities', 'Upcoming Activities')

  // Names the button that leads to the debrief in its own words, and its page when the player is somewhere else.
  const joinHint = isGamerUpcomingActivitiesPagePath(pathname)
    ? t('gamer.notifications.joinWithReflectionLobbyButton', 'Join your team with the "{{button}}" button.', {
        button: lobbyButtonLabel
      })
    : t(
        'gamer.notifications.joinWithReflectionLobbyButtonOnPage',
        'Join your team with the "{{button}}" button on the {{page}} page.',
        { button: lobbyButtonLabel, page: lobbyPageName }
      )

  const handleDismiss = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onDismiss()
    },
    [onDismiss]
  )

  return (
    <Box data-testid={TESTIDS.DEBRIEF_STARTED_NOTIFICATION}>
      <NotificationCard
        to={GamerRoutes.AI_COACH}
        icon={
          <SparklesIcon
            style={{
              width: COMPONENT_DIMENSIONS.ICON_MD,
              height: COMPONENT_DIMENSIONS.ICON_MD,
              color: INTERAX_COLORS.sun
            }}
            aria-hidden
          />
        }
        title={t('gamer.notifications.debriefStarted', 'Your AI debrief has started')}
        subtitle={joinHint}
        onCloseClick={handleDismiss}
        isVisible
        closeButtonAriaLabel={t('common.close', 'Close')}
      />
    </Box>
  )
}
